import { FC, ReactNode } from "react";

import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import { APP_BREAKPOINT } from "src/constants/layout";
import { getSupportedUserLocale } from "src/lib/i18next";
import translate from "src/lib/translate";
import MealsStatsCubit, {
  macroFillFraction
} from "src/state/MealsStatsCubit/MealsStatsCubit";
import Translate from "src/ui/components/Translate/Translate";
import { Ring, type RingSegment } from "@9amhealth/shared";

import { isCalorieOvershoot } from "./mealInsightHelpers";
import ProteinTargetBadge from "./ProteinTargetBadge";

const GREEN_GRADIENT_STOPS = ["#B8CF1F", "#6FC696"];
const GREEN_FILL_OPACITY = 0.8;
const RING_GRADIENT_SWEEP_DEG = 304.31;
const RING_SIZE_PX = 88;
const RING_SEGMENTS: RingSegment[] = [
  [2.76, 284.93],
  [299.17, 347.12]
];

const BAR_GRADIENT = `linear-gradient(90deg, ${GREEN_GRADIENT_STOPS[0]} 0%, ${GREEN_GRADIENT_STOPS[1]} 100%)`;

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--color-cream-darker, #f2efe7);
  border-radius: 0 0 1.5rem 1.5rem;
  padding: 3.5rem 1rem 1rem;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    padding: 3.5rem 1.5rem 1.5rem;
  }
`;

const Label = styled.p`
  margin: 0;
  color: var(--color-charcoal-60);
  font-size: 0.65625rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.42px;
  text-transform: uppercase;
`;

const ProteinRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const ProteinText = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const RingCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RingGoalLabel = styled.p`
  margin: 0;
  color: var(--color-charcoal-60);
  text-align: center;
  font-family: "STK Bureau";
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
`;

const RingAmount = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau Serif", serif;
  font-size: 1.875rem;
  font-weight: 300;
  line-height: 1.75rem;
  letter-spacing: -0.6px;
`;

const RingUnit = styled.p`
  margin: 0;
  color: var(--color-charcoal-60);
  text-align: center;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau";
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
`;

const ProteinDescription = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
  font-size: 0.875rem;
  line-height: 1.43;
  letter-spacing: -0.28px;
`;

const Tiles = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0.5rem 1rem 0;
`;

const Tile = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
`;

const Bar = styled.div`
  display: flex;
  height: 6px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-cream, #fffcf3);
`;

const BarTrack = styled.div`
  position: relative;
  flex: 1 0 0;
  min-width: 0;
`;

const BarFill = styled.div<{ $fraction: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(props) => props.$fraction * 100}%;
  border-radius: 8px;
  opacity: ${GREEN_FILL_OPACITY};
  background-image: ${BAR_GRADIENT};
  background-size: ${(props) =>
      props.$fraction > 0 ? 100 / props.$fraction : 100}%
    100%;

  &[data-overshoot="true"] {
    border-radius: 8px 0 0 8px;
  }
`;

const BarOvershoot = styled.div`
  flex: 0.33 0 0;
  background: var(--color-sunrise-orange, #ffbd70);
`;

const TileValue = styled.p`
  margin: 0;
  color: var(--color-charcoal, #212121);
  letter-spacing: -0.32px;
`;

const TileAmount = styled.span`
  color: var(--color-charcoal, #212121);
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau Serif", serif;
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1.75rem;
  letter-spacing: -0.48px;
`;

const TileUnit = styled.span`
  color: var(--color-charcoal-60);
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau";
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
`;

interface MacroTileConfig {
  key: "fiber" | "calories";
  labelKey:
    | "mealInsights.today.macro.fiber"
    | "mealInsights.today.macro.calories";
  amount: string;
  unit: string;
  fraction: number;
  /** Renders the trailing orange segment. Only calories have an upper bound. */
  overshoot: boolean;
}

const MealTodayStats: FC = () => {
  const [{ protein, fiber, calories, proteinHint }] =
    useBlocNext(MealsStatsCubit);

  const proteinFraction = macroFillFraction(protein.total, protein.floor);
  const proteinTargetMet = protein.floor > 0 && protein.total >= protein.floor;
  const proteinNotStarted = protein.floor > 0 && protein.total <= 0;

  // Before the first meal the ring names the goal instead of a running total.
  const ringAmount = Math.round(
    proteinNotStarted ? protein.floor : protein.total
  );
  const ringUnit =
    proteinNotStarted || protein.floor <= 0
      ? "g"
      : `/${Math.round(protein.floor)}g`;

  const ringSegments = proteinFraction > 0 ? RING_SEGMENTS : undefined;

  const proteinDescription =
    proteinHint ||
    (proteinTargetMet
      ? translate("mealInsights.today.proteinDone", {
          total: Math.round(protein.total)
        })
      : translate("mealInsights.today.proteinDescription", {
          floor: Math.round(protein.floor)
        }));

  const localeCalories = (value: number): string =>
    Math.round(value).toLocaleString(getSupportedUserLocale());

  const tiles: MacroTileConfig[] = [
    {
      key: "fiber",
      labelKey: "mealInsights.today.macro.fiber",
      amount: `${Math.round(fiber.total)}`,
      unit: `/${Math.round(fiber.floor)}g`,
      fraction: macroFillFraction(fiber.total, fiber.floor),
      overshoot: false
    },
    {
      key: "calories",
      labelKey: "mealInsights.today.macro.calories",
      amount: localeCalories(calories.total),
      unit:
        calories.target > 0
          ? `/${localeCalories(calories.target)}kcal`
          : "kcal",
      fraction: macroFillFraction(calories.total, calories.target),
      overshoot: isCalorieOvershoot(calories)
    }
  ];

  const proteinValue: ReactNode = (
    <RingCenter>
      {proteinNotStarted && (
        <RingGoalLabel>
          <Translate msg="mealInsights.today.proteinGoal" />
        </RingGoalLabel>
      )}
      <RingAmount>{ringAmount}</RingAmount>
      <RingUnit>{ringUnit}</RingUnit>
    </RingCenter>
  );

  return (
    <Block>
      <ProteinRow>
        {proteinTargetMet ? (
          <ProteinTargetBadge>{proteinValue}</ProteinTargetBadge>
        ) : (
          <Ring
            progress={proteinFraction}
            size={RING_SIZE_PX}
            progressGradient={GREEN_GRADIENT_STOPS}
            gradientSweep={RING_GRADIENT_SWEEP_DEG}
            segments={ringSegments}
            progressOpacity={GREEN_FILL_OPACITY}
            trackColor="var(--color-cream, #fffcf3)"
          >
            {proteinValue}
          </Ring>
        )}
        <ProteinText>
          <Label>
            <Translate msg="mealInsights.today.proteinLabel" />
          </Label>
          <ProteinDescription>{proteinDescription}</ProteinDescription>
        </ProteinText>
      </ProteinRow>

      <Tiles>
        {tiles.map((tile) => (
          <Tile key={tile.key}>
            <Label>
              <Translate msg={tile.labelKey} />
            </Label>
            <Bar>
              <BarTrack>
                <BarFill
                  $fraction={tile.fraction}
                  data-overshoot={tile.overshoot ? "true" : "false"}
                />
              </BarTrack>
              {tile.overshoot && <BarOvershoot />}
            </Bar>
            <TileValue>
              <TileAmount>{tile.amount}</TileAmount>
              <TileUnit>{tile.unit}</TileUnit>
            </TileValue>
          </Tile>
        ))}
      </Tiles>
    </Block>
  );
};

export default MealTodayStats;
